import { DebugLogger } from '@affine/debug';
import { BRAND_AI_DISPLAY_NAME, BRAND_DISPLAY_NAME } from '@streamlain/brand';
import type { BackendModule, i18n } from 'i18next';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import type { useStreamLainI18N } from './i18n.gen';
import type { Language } from './resources';
import { SUPPORTED_LANGUAGES } from './resources';

const logger = new DebugLogger('i18n');

const defaultLng: Language = 'en';
const BRAND_RESOURCE_KEY = '__brand.name';
/** The AI's name in every text: $t(__ai.name), the own one $t(__ai.own). */
const AI_NAME_RESOURCE_KEY = '__ai.name';
const AI_OWN_NAME_RESOURCE_KEY = '__ai.own';
export const AI_DISPLAY_NAME = BRAND_AI_DISPLAY_NAME;
const AI_OWN_NAMES: Partial<Record<Language, string>> = {
  de: `Dein ${AI_DISPLAY_NAME}`,
  en: `Your ${AI_DISPLAY_NAME}`,
};

/** Names every text takes from one source instead of spelling them out. */
function withNameResources(lng: Language, resource: Record<string, string>) {
  return {
    ...resource,
    [BRAND_RESOURCE_KEY]: BRAND_DISPLAY_NAME,
    [AI_NAME_RESOURCE_KEY]: AI_DISPLAY_NAME,
    [AI_OWN_NAME_RESOURCE_KEY]: AI_OWN_NAMES[lng] ?? AI_OWN_NAMES[defaultLng],
  };
}

function getBundledDefaultResource(): Record<string, string> {
  const resource = SUPPORTED_LANGUAGES[defaultLng].resource;
  if (typeof resource === 'function') {
    throw new TypeError('The default i18n resource must be bundled.');
  }
  return resource;
}

function normalizeLanguage(lng: string | undefined): Language | undefined {
  return lng && SUPPORTED_LANGUAGES[lng as Language]
    ? (lng as Language)
    : undefined;
}

let _instance: i18n | null = null;
export const getOrCreateI18n = (initialLng?: Language): i18n => {
  const lng = normalizeLanguage(initialLng) ?? defaultLng;
  if (!_instance) {
    _instance = i18next.createInstance();
    _instance
      .use(initReactI18next)
      .use({
        type: 'backend',
        init: () => {},
        read: (lng: Language, _ns: string, callback) => {
          const resource = SUPPORTED_LANGUAGES[lng].resource;
          if (typeof resource === 'function') {
            resource()
              .then(data => {
                logger.info(`Loaded i18n ${lng} resource`);
                callback(null, withNameResources(lng, data.default));
              })
              .catch(err => {
                logger.error(`Failed to load i18n ${lng} resource`, err);
                callback(null, null);
              });
          } else {
            callback(null, withNameResources(lng, resource));
          }
        },
      } as BackendModule)
      .init({
        lng,
        fallbackLng: code => {
          // always fallback to english
          const fallbacks: string[] = [defaultLng];
          const langPart = code?.split('-')[0] ?? defaultLng;

          // fallback xx-YY to xx, e.g. es-AR to es
          // fallback zh-Hant to zh-Hans
          if (langPart === 'cn') {
            fallbacks.push('zh-Hans');
          } else if (
            langPart !== code &&
            SUPPORTED_LANGUAGES[code as Language]
          ) {
            fallbacks.unshift(langPart ?? code);
          }

          return fallbacks;
        },
        supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
        debug: false,
        partialBundledLanguages: true,
        resources: {
          [defaultLng]: {
            translation: withNameResources(
              defaultLng,
              getBundledDefaultResource()
            ),
          },
        },
        interpolation: {
          escapeValue: false, // not needed for react as it escapes by default
        },
      })
      .then(() => {
        logger.info('i18n initialized');
      })
      .catch(() => {});
  } else {
    const nextLng = normalizeLanguage(initialLng);
    if (nextLng && _instance.language !== nextLng) {
      _instance.changeLanguage(nextLng).catch(err => {
        logger.error(
          `Failed to switch i18n to initial ${nextLng} resource`,
          err
        );
      });
    }
  }

  return _instance;
};

declare module 'i18next' {
  interface CustomTypeOptions {
    // NOTE(@forehalo):
    //   DO NOT ENABLE THIS
    //   This could bring typecheck for <Trans /> component,
    //   but it will make typecheck of whole codebase so laggy!
    //   check [./react.ts]
    // resources: {
    //   translation: LanguageResource;
    // };
  }
}

export type I18nFuncs = ReturnType<typeof useStreamLainI18N>;
type KnownI18nKey = keyof I18nFuncs;

export type I18nString =
  | KnownI18nKey
  | string
  | { i18nKey: string; options?: Record<string, any> };

export function isI18nString(value: unknown): value is I18nString {
  if (typeof value === 'string') {
    return true;
  }

  if (typeof value === 'object' && value !== null) {
    return 'i18nKey' in value;
  }

  return false;
}

function createI18nMethods(getI18nFn: () => i18n) {
  return {
    t(key: I18nString, options?: Record<string, any>) {
      if (typeof key === 'object' && 'i18nKey' in key) {
        options = key.options;
        key = key.i18nKey as string;
      }

      const i18n = getI18nFn();
      // Plural-only keys (`x_one`/`x_other`) exist only for a given count.
      if (i18n.exists(key, options)) {
        return i18n.t(key, options);
      } else {
        // unknown translate key 'xxx.xxx' returns itself
        return key;
      }
    },
    get language() {
      const i18n = getI18nFn();
      return i18n.language;
    },
    changeLanguage(lng?: string | undefined) {
      const i18n = getI18nFn();
      return i18n.changeLanguage(lng);
    },
    get on() {
      const i18n = getI18nFn();
      return i18n.on.bind(i18n);
    },
  };
}

/** Methods every translator carries next to its translation keys. */
export type I18nMethods = ReturnType<typeof createI18nMethods>;

/**
 * Translator from `createI18nWrapper`. It is a named type, so a hook that
 * returns it is declared by this name instead of inlining every key.
 */
export type I18nTranslator = I18nMethods &
  I18nFuncs & {
    [unknownKey: string]: (...args: any[]) => string;
  };

export function createI18nWrapper(getI18nFn: () => i18n): I18nTranslator {
  const I18nMethod = createI18nMethods(getI18nFn);
  return new Proxy(I18nMethod, {
    get(self, key: string) {
      if (key in self) {
        // @ts-expect-error allow
        return self[key];
      }

      return I18nMethod.t.bind(null, key);
    },
    has(self, key: string) {
      if (key in self) {
        return true;
      }
      const i18n = getI18nFn();
      if (i18n.exists(key)) {
        return true;
      }
      return false;
    },
  }) as I18nTranslator;
}

/**
 * I18n['com.streamlain.xxx']({ arg1: 'hello' }) -> '中文 hello'
 */
export const I18n = createI18nWrapper(getOrCreateI18n);
export type I18nInstance = typeof I18n;
